/**
 * `choice` — pick one. The whole grading rule is an id comparison, which is the
 * point: the *hard* part of a choice item is rendering it accessibly, and that is
 * the asset's job and the validator's check, not the scorer's.
 */

import { isChoiceResponse } from '@cet/asset-contract';
import type { ChoiceSchemeDoc, ItemResponse, ItemVerdict, Locale } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { graded, unanswered } from '../verdicts.js';

export function scoreChoice(
  itemId: string,
  scheme: ChoiceSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isChoiceResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is a choice item, so its response must be { choiceId: string }. ` +
        `Received keys: ${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  // Note what is NOT checked here: whether `choiceId` names a choice that exists
  // in the content. The scorer never sees the content — that agreement is a
  // contract rule enforced at authoring time, where it can be fixed. An id that
  // matches nothing is simply wrong, which is also what it would be if a learner
  // tampered with the value.
  const correct = response.choiceId === scheme.correct;
  return graded(
    itemId,
    { awarded: correct ? scheme.points : 0, max: scheme.points },
    scheme.feedback,
    correct,
    locale,
  );
}
